import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useQueryClient } from "@tanstack/react-query";
import { CreditCardIcon, DevicePhoneMobileIcon } from "@heroicons/react/24/outline";

import { SearchAccount } from "../../components/accounts/SearchAccount";
import { ButtonInput } from "../../components/shared/Inputs/ButtonInput";
import SelectInput from "../../components/shared/Inputs/SelectInput";
import SwitchInput from "../../components/shared/Inputs/SwitchInput";
import LoadingWheel from "../../components/shared/LoadingWheel";
import StatusPill, { StatusPillColour } from "../../components/shared/StatusPill";
import { usePageNameContext } from "../../contexts/PageTitleContext";
import useAccount from "../../data/useAccount";
import useSettings from "../../data/useSettings";
import useShippingPolicies from "../../data/useShippingPolicies";
import useTill from "../../data/useTill";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import { IAccount } from "../../models/account/IAccount";
import { ISetting, SettingInputType, SettingType } from "../../models/Setting";
import { IShippingPolicy } from "../../models/ShippingPolicy";
import PriceFormat from "../../utils/priceFormat";

const KEYS = {
  account: "Till:WalkInAccountId",
  policy: "Till:CollectedShippingPolicyId",
  footer: "Till:ReceiptFooter",
  disableCash: "Till:DisableCash",
} as const;

interface FormValues {
  shippingPolicyId: string;
  disableCash: boolean;
  receiptFooter: string;
}

// The till's own settings, with pickers instead of raw GUIDs. Loading the till config makes the API
// create the default account/policy and the settings themselves, so nothing has to exist beforehand.
export default function TillSettings() {
  const { setInfo } = usePageNameContext();
  const queryClient = useQueryClient();
  const { settings, loading, update, create } = useSettings();
  const { config } = useTill("TillSettings");
  const { shippingPolicies } = useShippingPolicies();
  const { saveHandlers } = useDefaultCRUDHandlers("Till settings");
  const [selectedAccount, setSelectedAccount] = useState<IAccount | null>(null);

  useEffect(() => {
    setInfo({ name: "Till", desc: "Defaults, receipts and card readers for the till" });
  }, [setInfo]);

  // The config call provisions any missing settings; re-read them once it has.
  useEffect(() => {
    if (config.data) queryClient.invalidateQueries(["settings"]);
  }, [config.data, queryClient]);

  const byKey = useMemo(() => {
    const map = new Map<string, ISetting>();
    settings.forEach((s) => map.set(s.key, s));
    return map;
  }, [settings]);

  const accountSetting = byKey.get(KEYS.account);
  const policySetting = byKey.get(KEYS.policy);
  const footerSetting = byKey.get(KEYS.footer);
  const disableCashSetting = byKey.get(KEYS.disableCash);

  const { register, setValue, watch, reset } = useForm<FormValues>({
    defaultValues: { shippingPolicyId: "", disableCash: false, receiptFooter: "" },
  });

  const cfg = config.data?.data;

  // The settings list is the thing we write to; the till config is what the till actually uses.
  // Prefer the setting's value, fall back to the config so the page is never blank.
  useEffect(() => {
    reset({
      shippingPolicyId: policySetting?.value ?? cfg?.defaultShippingPolicyId ?? "",
      disableCash: disableCashSetting ? disableCashSetting.value.trim().toLowerCase() === "true" : cfg?.cashEnabled === false,
      receiptFooter: footerSetting?.value ?? cfg?.receiptFooter ?? "",
    });
  }, [policySetting?.value, disableCashSetting, footerSetting?.value, cfg?.defaultShippingPolicyId, cfg?.cashEnabled, cfg?.receiptFooter, reset]);

  const accountIdInUse = accountSetting?.value || cfg?.defaultWalkInAccountId || undefined;
  const currentAccount = useAccount(selectedAccount ? undefined : accountIdInUse, "TillSettings");
  useEffect(() => {
    if (!selectedAccount && currentAccount.account.data?.data) setSelectedAccount(currentAccount.account.data.data);
  }, [currentAccount.account.data, selectedAccount]);

  // Update when the setting exists, otherwise create it: the API normally provisions these on the
  // till's first load, but a stale settings cache must not stop an admin saving.
  const save = (setting: ISetting | undefined, key: string, value: string, inputType: SettingInputType = SettingInputType.Text) => {
    if (setting) {
      if (setting.value === value) return;
      update.mutate({ ...setting, value }, saveHandlers);
      return;
    }
    create.mutate(
      { key, value, settingType: SettingType.General, inputType, required: false, isVisibleToCustomer: true } as unknown as ISetting,
      saveHandlers
    );
  };

  const onAccountSelect = (account: IAccount | null) => {
    // Clearing the search box hands back null; that is not a choice, so keep the saved account.
    setSelectedAccount(account);
    if (account?.id) save(accountSetting, KEYS.account, account.id);
  };

  const onSave = () => {
    save(policySetting, KEYS.policy, watch("shippingPolicyId"));
    save(disableCashSetting, KEYS.disableCash, watch("disableCash") ? "true" : "false", SettingInputType.Toggle);
    save(footerSetting, KEYS.footer, watch("receiptFooter").trim());
  };

  const policies: IShippingPolicy[] = shippingPolicies.data?.data ?? [];
  const policyOptions = policies.map((p) => ({ id: p.id, name: `${p.name} (${PriceFormat(p.price)})` }));
  const footerPreview = watch("receiptFooter");

  if (loading || config.isLoading) return <LoadingWheel />;

  return (
    <div className="space-y-8">
      {config.data && !config.data.success ? (
        <p className="rounded-md bg-amber-50 p-3 text-sm text-amber-800">{config.data.message}</p>
      ) : null}

      <section className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900">Sale defaults</h2>
        <p className="mt-1 text-sm text-gray-500">
          Every till sale is invoiced to this account and uses this delivery option unless the operator picks something else.
          Wilma created <span className="font-medium">Till Sale Default Account</span> and the free{" "}
          <span className="font-medium">Till Sale Default Shipping</span> option for you; swap them for your own here if you prefer.
        </p>

        <div className="mt-4 grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div>
            <SearchAccount label="Walk-in customer account" onAccountSelect={onAccountSelect} selectedAccount={selectedAccount} />
            <p className="mt-1 text-xs text-gray-500">Saved as soon as you pick one.</p>
          </div>
          <div>
            <SelectInput
              label="Delivery option for counter sales"
              register={register}
              registerName="shippingPolicyId"
              options={policyOptions}
              setValue={setValue}
              watch={watch}
              placeholder="Choose a shipping policy"
            />
          </div>
        </div>
      </section>

      <section className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900">Payments</h2>
        <div className="mt-3">
          <SwitchInput label="Turn cash off (card and bank transfer only)" register={register} registerName="disableCash" setValue={setValue} watch={watch} />
        </div>

        <div className="mt-6 rounded-md border border-gray-200 p-4">
          <div className="flex items-center gap-2">
            <CreditCardIcon className="h-5 w-5 text-gray-500" />
            <h3 className="font-medium text-gray-900">Stripe card readers</h3>
            {cfg ? <StatusPill text={cfg.stripeEnabled ? "Connected" : "Not connected"} colour={cfg.stripeEnabled ? StatusPillColour.Green : StatusPillColour.Yellow} /> : null}
          </div>
          {cfg?.stripeMessage ? <p className="mt-2 text-sm text-amber-800">{cfg.stripeMessage}</p> : null}
          {cfg?.stripeEnabled && cfg.readers.length === 0 ? (
            <p className="mt-2 text-sm text-gray-600">No readers found. Register one in your Stripe Dashboard under Terminal → Readers.</p>
          ) : null}
          {cfg?.readers?.length ? (
            <ul className="mt-3 divide-y divide-gray-100">
              {cfg.readers.map((r) => (
                <li key={r.id} className="flex items-center gap-3 py-2">
                  <DevicePhoneMobileIcon className="h-5 w-5 text-gray-400" />
                  <span className="flex-1 text-sm text-gray-900">{r.label || r.id} <span className="text-gray-500">· {r.deviceType}</span></span>
                  <StatusPill text={r.status === "online" ? "Online" : "Offline"} colour={r.status === "online" ? StatusPillColour.Green : StatusPillColour.Red} />
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      </section>

      <section className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900">Receipt footer</h2>
        <p className="mt-1 text-sm text-gray-500">Printed at the bottom of every till receipt. Returns policy, opening hours, a thank-you: keep it short, receipts are 80mm wide.</p>
        <div className="mt-4 grid grid-cols-1 gap-6 lg:grid-cols-2">
          <div>
            <label htmlFor="till-footer" className="block text-sm font-medium text-gray-700">Footer text</label>
            <textarea
              id="till-footer"
              rows={5}
              maxLength={500}
              {...register("receiptFooter")}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              placeholder="Thank you for your business. Returns within 14 days with this receipt."
            />
            <p className="mt-1 text-xs text-gray-500">{footerPreview.length}/500</p>
          </div>
          <div>
            <p className="block text-sm font-medium text-gray-700">Preview</p>
            <div className="mt-1 mx-auto w-[72mm] rounded border border-dashed border-gray-300 bg-white p-3 font-mono text-[12px] leading-tight text-gray-800">
              <p className="text-center font-semibold">Your business name</p>
              <p className="my-2 border-t border-dashed border-gray-400" />
              <div className="flex justify-between"><span>Door mirror</span><span>£60.00</span></div>
              <p className="my-2 border-t border-dashed border-gray-400" />
              <div className="flex justify-between font-semibold"><span>TOTAL</span><span>£60.00</span></div>
              <p className="my-2 border-t border-dashed border-gray-400" />
              <p className="whitespace-pre-wrap text-center text-[11px]">{footerPreview || <span className="text-gray-400">Your footer appears here</span>}</p>
            </div>
          </div>
        </div>
      </section>

      <div className="flex justify-end">
        <ButtonInput label="Save till settings" classes="" onClick={onSave} isLoading={update.isLoading || create.isLoading} />
      </div>
    </div>
  );
}
